import type { ReactNode } from 'react'

import {
  type PetDiscount,
  PLAN_DURATION_MONTHS,
  type PlanPerk,
  type PortalPlan,
  REGIONS_BY_ID,
  type RegionId,
  type SubscriptionPlan,
} from '@snootsvet/shared/portal'

/**
 * How each perk reads. `GET /plans` decides which plans a region sells and the perks each
 * advertises; ReactNode so copy can emphasise part of a line.
 */
export const PERK_COPY: Record<PlanPerk, ReactNode> = {
  wellness_screen: <span>Wellness screen included</span>,
}

/**
 * Preferred starting selection: annual is long enough to be worth the discount, short enough
 * not to spook.
 */
const DEFAULT_PLAN: SubscriptionPlan = 'annual'

/** `DEFAULT_PLAN` when the region sells it, otherwise the first plan offered. */
export function initialPlan(plans: readonly PortalPlan[]): SubscriptionPlan | null {
  return plans.some((p) => p.plan === DEFAULT_PLAN) ? DEFAULT_PLAN : (plans[0]?.plan ?? null)
}

const PLAN_BILLING_CADENCE: Record<SubscriptionPlan, string> = {
  monthly: 'monthly (12 month commitment)',
  // Annual and two-yearly roll over to monthly rather than renewing, so the bill covers one term.
  annual: 'for the year',
  two_yearly: 'for 2 years',
}

/** Names each kind of discount after its percent, e.g. "10% multi-pet discount". */
export const DISCOUNT_LABELS: Record<PetDiscount['kind'], string> = {
  multi_pet: 'multi-pet discount',
}

/** The discount that covers `plan`, if any; the first one wins when several do. */
export function findPlanDiscount(
  discounts: readonly PetDiscount[],
  plan: SubscriptionPlan
): PetDiscount | undefined {
  return discounts.find((discount) => discount.plans.includes(plan))
}

export type PlanPriceCopy = {
  /** Per-month price, e.g. "£33". */
  price: string
  /** Fine print, e.g. "Billed £396 for the year". */
  billing: string
  /** "Save £36 vs monthly", for plans that work out cheaper than monthly. */
  savings: string | null
}

/**
 * Display strings for `plan` at its live price, optionally after a percent-off discount.
 * Returns `null` when the region doesn't sell the plan.
 */
export function describePlanPrice(
  plans: readonly PortalPlan[],
  plan: SubscriptionPlan,
  region: RegionId,
  percentOff?: number
): PlanPriceCopy | null {
  const fullAmount = plans.find((p) => p.plan === plan)?.amount
  if (fullAmount === undefined) return null

  const amount = percentOff ? Math.round((fullAmount * (100 - percentOff)) / 100) : fullAmount
  return describePlanAmount(plans, plan, region, amount)
}

/** Display strings for `plan` billed at `amount`, a price Stripe has already discounted. */
export function describePlanAmount(
  plans: readonly PortalPlan[],
  plan: SubscriptionPlan,
  region: RegionId,
  amount: number
): PlanPriceCopy {
  const months = PLAN_DURATION_MONTHS[plan]
  const monthlyAmount = plans.find((p) => p.plan === 'monthly')?.amount
  const savings = monthlyAmount === undefined ? 0 : monthlyAmount * months - amount

  return {
    price: formatPlanPrice(Math.round(amount / months), region),
    billing: `Billed ${formatPlanPrice(amount, region)} ${PLAN_BILLING_CADENCE[plan]}`,
    savings: savings > 0 ? `Save ${formatPlanPrice(savings, region)} vs monthly` : null,
  }
}

/** Like `formatRegionPrice`, but drops the pennies from whole amounts: "£33", "£29.70". */
function formatPlanPrice(minorUnits: number, region: RegionId): string {
  const { locale, currency } = REGIONS_BY_ID[region]
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
  }).format(minorUnits / 100)
}
